import React, { useCallback, useRef, useState } from 'react';
import { Typography } from 'antd';
import { InboxOutlined } from '@ant-design/icons';

import { useUIColors } from '../../theme';
import { cx, type Styleable } from '../../variants';

const { Text } = Typography;

export interface ImportDropzoneProps extends Styleable {
    /** Fired with the raw selected `File[]` for both drag-drop and click-to-browse. No transport. */
    onFilesSelected: (files: File[]) => void;
    /** Native `<input accept>`, e.g. `.xlsx`. */
    accept?: string;
    multiple?: boolean;
    disabled?: boolean;
    /** Caller-supplied primary hint, e.g. "Kéo file .xlsx vào đây". */
    hint: React.ReactNode;
    /** Caller-supplied secondary line — a size note, a "tải mẫu" link element, etc. */
    subHint?: React.ReactNode;
}

/**
 * EAA-1804: the component sheet's "Import Dropzone" half of the "Import
 * Dropzone · Job Progress" pattern — a pure file-selection surface for a bulk
 * background import (spreadsheet-of-many-records), paired with
 * `JobProgressBar` once a job starts.
 *
 * Deliberately NOT built on antd `Upload`/`Upload.Dragger`: that component's
 * upload-list/request machinery (internal `fileList` state, the
 * `beforeUpload`/`customRequest` lifecycle) exists to drive an upload, and this
 * component must never perform one — it only ever hands the caller a raw
 * `File[]`. Reusing it would mean fighting `onChange` firing once per file and
 * an internal list that accumulates across selections just to throw the
 * result away. Native HTML5 drag events plus a hidden `<input type="file">`
 * give the same drag-and-drop/click-to-browse mechanics with none of that, and
 * the icon/hint/sub-hint layout still matches `DocumentUpload`'s dragger for
 * visual consistency.
 */
export function ImportDropzone({
    onFilesSelected,
    accept,
    multiple = true,
    disabled = false,
    hint,
    subHint,
    className,
    style,
}: ImportDropzoneProps): React.ReactElement {
    const colors = useUIColors();
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    const emitFiles = useCallback(
        (fileList: FileList | null) => {
            if (!fileList || fileList.length === 0) return;
            onFilesSelected(Array.from(fileList));
        },
        [onFilesSelected]
    );

    const handleDrop = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            setIsDragging(false);
            if (disabled) return;
            emitFiles(event.dataTransfer.files);
        },
        [disabled, emitFiles]
    );

    const handleInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            emitFiles(event.target.files);
            // EAA-1804: reset so re-selecting the identical file still fires `change`.
            event.target.value = '';
        },
        [emitFiles]
    );

    const handleClick = useCallback(() => {
        if (!disabled) inputRef.current?.click();
    }, [disabled]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (disabled) return;
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                inputRef.current?.click();
            }
        },
        [disabled]
    );

    return (
        <div
            className={cx(
                'ui-import-dropzone',
                isDragging && 'ui-import-dropzone--dragging',
                disabled && 'ui-import-dropzone--disabled',
                className
            )}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onDragOver={(event) => {
                event.preventDefault();
                if (!disabled) setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            style={{
                border: `1px dashed ${isDragging ? colors.primary : colors.border}`,
                borderRadius: 8,
                padding: '32px 16px',
                textAlign: 'center',
                cursor: disabled ? 'not-allowed' : 'pointer',
                background: isDragging ? colors.surfaceHover : colors.surface,
                opacity: disabled ? 0.6 : 1,
                ...style,
            }}
        >
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                multiple={multiple}
                disabled={disabled}
                onChange={handleInputChange}
                style={{ display: 'none' }}
                aria-hidden="true"
                tabIndex={-1}
            />
            <p style={{ marginBottom: 8 }}>
                <InboxOutlined style={{ fontSize: 32, color: colors.primary }} />
            </p>
            <Text style={{ display: 'block', color: colors.text }}>{hint}</Text>
            {subHint && (
                <Text
                    style={{
                        display: 'block',
                        color: colors.textMuted,
                        fontSize: 12,
                        marginTop: 4,
                    }}
                >
                    {subHint}
                </Text>
            )}
        </div>
    );
}

export default ImportDropzone;
